import React from 'react';
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  MenuItem,
  Select,
  Chip,
  Box,
  FormHelperText,
} from '@mui/material';

/**
 * FormMultiSelect - Reusable multi-select dropdown component
 * @param {Object} props
 * @param {string} props.name - Field name attribute
 * @param {string} props.label - Field label
 * @param {Array} props.value - Current selected values (array)
 * @param {Array} props.options - Array of options [{label: '', value: ''}, ...]
 * @param {Function} props.onChange - Change handler
 * @param {Function} [props.onBlur] - Blur handler
 * @param {boolean} [props.required] - Whether field is required
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {boolean} [props.error] - Show error state
 * @param {string} [props.helperText] - Helper/error text
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.variant] - FormControl variant (outlined, standard, filled)
 * @param {string} [props.size] - Input size (small, medium)
 * @param {boolean} [props.showChips] - Show selected values as chips (default: true)
 * @param {string} [props.className] - Additional CSS class
 * @param {Object} [props.sx] - MUI sx prop for styling
 * @param {Object} [props.rest] - Other Select props
 */
const FormMultiSelect = React.forwardRef((props, ref) => {
  const {
    name,
    label,
    value = [],
    options = [],
    onChange,
    onBlur,
    required = false,
    disabled = false,
    error = false,
    helperText = '',
    placeholder = '',
    variant = 'outlined',
    size = 'small',
    showChips = true,
    className,
    sx = {},
    ...rest
  } = props;

  const selectedValues = Array.isArray(value) ? value : [];

  const handleChange = (event) => {
    const {
      target: { value: newValue },
    } = event;
    // newValue is an array when multiple is true
    onChange?.(event);
  };

  const renderValue = (selected) => {
    if (!showChips) {
      return Array.isArray(selected)
        ? selected.map(v => options.find(o => o.value === v)?.label || v).join(', ')
        : selected;
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {Array.isArray(selected) &&
          selected.map((selectedValue) => {
            const option = options.find((o) => o.value === selectedValue);
            return (
              <Chip
                key={selectedValue}
                label={option?.label || selectedValue}
                size="small"
                onDelete={(e) => {
                  e.stopPropagation();
                  const newSelected = selected.filter((v) => v !== selectedValue);
                  const syntheticEvent = {
                    target: {
                      name: name,
                      value: newSelected,
                    },
                  };
                  onChange?.(syntheticEvent);
                }}
                disabled={disabled}
              />
            );
          })}
      </Box>
    );
  };

  return (
    <Box className={className}>
      <FormControl
        ref={ref}
        variant={variant}
        required={required}
        disabled={disabled}
        error={error}
        fullWidth
        size={size}
        sx={sx}
      >
        <InputLabel>{label}</InputLabel>
        <Select
          name={name}
          multiple
          value={selectedValues}
          onChange={handleChange}
          onBlur={onBlur}
          label={label}
          renderValue={renderValue}
          {...rest}
        >
          {placeholder && (
            <MenuItem value="" disabled>
              {placeholder}
            </MenuItem>
          )}
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={option.disabled || false}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </Box>
  );
});

FormMultiSelect.displayName = 'FormMultiSelect';

export default FormMultiSelect;
